const feature = {
  slug: "compare",
  path: "/compare",
  eyebrow: "Compare",
  h1: "Checkout DM vs other Instagram DM automation tools",
  seoTitle: "Checkout DM vs ManyChat and Other Instagram DM Tools",
  description:
    "How Checkout DM compares with ManyChat, InstantDM and ReplyRush for Instagram sellers: checkout, payments, orders and where the other tools are stronger.",
  intro:
    "Instagram DM automation tools look alike on the surface. They all reply to comments and Story replies. The real difference shows up after the first message: can the tool show a product, take a size, collect an address, accept payment and track the order? We make Checkout DM, so we say plainly where the others are stronger.",
  glance: [
    { label: "Compared", value: "ManyChat, InstantDM, ReplyRush" },
    { label: "Source", value: "Vendors’ public pages, October 2026" },
    { label: "Made by", value: "Checkout DM" },
  ],
  blocks: [
    { type: "h2", text: "The short answer" },
    {
      type: "p",
      text:
        "If you sell products and want the whole sale to happen in the chat, Checkout DM covers the catalog, cart, address, payment and order tracking in one place. If you need chat automation across several channels, or you want to design flexible flows yourself, another tool may suit you better.",
    },

    { type: "h2", text: "Side by side" },
    {
      type: "table",
      headers: ["Feature", "Checkout DM", "ManyChat", "InstantDM", "ReplyRush"],
      rows: [
        ["Comment-to-DM automation", "Yes", "Yes", "Yes", "Yes"],
        ["Story reply automation", "Yes", "Yes", "Yes", "Yes"],
        ["Permanent free plan", "Yes", "Yes", "Yes", "Yes"],
        ["Priced by number of contacts", "No", "Yes", "No", "No"],
        ["Product catalog browsing in the chat", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Size choice with stock per size", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Cart and delivery address collected in the chat", "Yes", "Not listed", "Not listed", "Not listed"],
        ["UPI, QR and bank transfer payments", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Payment links (Razorpay, Stripe, PayPal)", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Orders dashboard with returns and exchanges", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Coupons and offers", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Courier integration (Shiprocket, Shippo)", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Storefront link to sell anywhere", "Yes", "Not listed", "Not listed", "Not listed"],
        ["Team members", "Yes", "Yes", "Yes", "Not listed"],
        ["WhatsApp, TikTok or SMS chat automation", "No", "Yes", "Not listed", "Not listed"],
      ],
    },
    {
      type: "callout",
      tone: "lilac",
      title: "How to read “Not listed”",
      text:
        "It means we could not find the feature listed as built in on the vendor’s public pages as of October 2026. It does not always mean it is impossible. A flexible tool such as ManyChat can approximate several of these with flows and integrations, which means you build it yourself instead of switching it on. Please confirm current features on each vendor’s own site. Our full write-up is in [Instagram DM automation tools compared](/blog/instagram-dm-automation-feature-comparison).",
    },

    { type: "h2", text: "Where Checkout DM is the better fit" },
    {
      type: "ul",
      items: [
        "**You take orders in the chat.** Catalog, sizes, stock, cart, address and payment are part of the product, not something you build.",
        "**You collect money by UPI, QR code or bank transfer.** Manual payments work from the Free plan, alongside Razorpay, Stripe and PayPal links.",
        "**You want orders, returns and shipping in one place.** The dashboard tracks them, and Shiprocket and Shippo connect for delivery status.",
        "**You do not want pricing that grows with your contact list.** Checkout DM plans are flat, with no commission or transaction fee.",
      ],
    },

    { type: "h2", text: "Where another tool is the better fit" },
    {
      type: "ul",
      items: [
        "**You need chat automation beyond Instagram.** Checkout DM’s chat automation runs on Instagram, while ManyChat lists WhatsApp, TikTok and SMS automation. Your Checkout DM Storefront link can still be shared on any channel.",
        "**You want to design custom flows and integrations.** A flexible flow builder gives more freedom than a checkout built for selling.",
        "**You never sell through the chat.** If you only capture leads or send replies, a simpler tool can be enough.",
      ],
    },

    { type: "h2", text: "What Checkout DM costs" },
    {
      type: "p",
      text:
        "The Free plan is ₹0 and allows 5 products and 15 unique customer conversations a month. Paid plans run from ₹499 to ₹6,499 a month, with no commission on any plan. See the [pricing page](/pricing), or read [which Checkout DM plan you should choose](/blog/which-checkout-dm-plan-should-you-choose).",
    },

    { type: "h2", text: "How to decide in a week" },
    {
      type: "ol",
      items: [
        "List what a customer must do between “is this available?” and “paid”. Count the steps you currently do by hand.",
        "Check which tool covers those steps without building them yourself.",
        "Test your favourite on its free plan with one real product and a test order from a second account.",
        "Choose the one that removed the most manual work, not the one with the longest feature list.",
      ],
    },
    {
      type: "p",
      text:
        "Looking for other options first? Read our guides to [ManyChat alternatives for Instagram sellers](/blog/manychat-alternatives-instagram-sellers) and the [best free Instagram automation tools](/blog/best-free-instagram-automation-tools).",
    },
  ],
  related: [
    { to: "/features", title: "All Checkout DM features", description: "What Checkout DM does, from first message to shipped order." },
    { to: "/pricing", title: "Checkout DM pricing", description: "Plans from Free to Pro, with no commission." },
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "See the full chat flow." },
    { to: "/faq", title: "Frequently asked questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "How is Checkout DM different from ManyChat?",
      a: "ManyChat is a general chat automation tool that lists WhatsApp, TikTok and SMS as well as Instagram, and its pricing is based on contacts. Checkout DM is built for selling on Instagram, so the catalog, cart, address, payment and order tracking are built in, with flat plans and no commission.",
    },
    {
      q: "Is Checkout DM only for Instagram?",
      a: "Chat automation works on Instagram only today, and sellers need an Instagram account to use it. But from the Creator plan, you can share your Storefront link on WhatsApp, Telegram, YouTube, Facebook or any website, and buyers can order from it without Instagram. If you need WhatsApp, TikTok or SMS chat automation, another tool is a better fit.",
    },
    {
      q: "Is there a free plan?",
      a: "Yes. Checkout DM has a Free plan at ₹0 with 5 products and 15 unique customer conversations a month, and the other tools in this comparison also list free plans.",
    },
    {
      q: "Does Checkout DM charge by the number of contacts?",
      a: "No. Checkout DM plans are flat monthly prices, and there is no commission or transaction fee on any plan. In the comparison above, ManyChat is the tool whose pricing is based on contacts.",
    },
    {
      q: "Where does this comparison come from?",
      a: "From the vendors’ public pages as of October 2026. We make Checkout DM, so please confirm each vendor’s current features on its own site before you decide.",
    },
  ],
};

export default feature;
